// Die Tastenbelegung — **eine** Quelle fuer README, Hilfe und Verdrahtung.
//
// Sie stand bisher dreimal: als lokale Karte in `main.ts`, als Textblock im
// Hilfe-Modal und als Tabelle im README. Drei Kopien driften, und beim
// Einfuehren dieser Datei war der Beweis schon da — dem Modal fehlten `Esc`
// und `H`, und vier Wortlaute wichen ab. Ein Spieler glaubt die falsche Kopie
// trotzdem.
//
// Gate FG-39 vergleicht `KEY_BINDINGS` Zeile fuer Zeile mit der Tabelle unter
// `## Steuerung` im README. Deshalb sind `keys` und `action` **wortgleich** mit
// den beiden Spalten dort; wer hier etwas aendert, aendert das README mit.

import type { PanelTab } from '../core/bus';

export interface KeyBinding {
  /** Wortgleich die linke Spalte der README-Tabelle. */
  keys: string;
  /** Wortgleich die rechte Spalte. */
  action: string;
  /** `event.key.toLowerCase()`, auf das `main.ts` hoert — sonst `null`. */
  key: string | null;
  /** Reiter, den die Taste umschaltet — sonst `null`. */
  tab: PanelTab | null;
}

/**
 * `X`, `F`, `V`, `P` und `+`/`-` tragen bewusst `key: null`.
 *
 * Sie werden in der Weltszene behandelt (`scenes/SpaceScene.ts`), und `scenes`
 * darf `ui` nicht importieren (Gate FG-04). Diese Tabelle **dokumentiert** sie
 * darum nur; verdrahtet sind sie dort. Das ist die ehrliche Fassung — eine
 * Bindung hier zu behaupten, die niemand liest, waere schlimmer.
 */
export const KEY_BINDINGS: readonly KeyBinding[] = [
  { keys: 'WASD / Pfeiltasten', action: 'fliegen', key: null, tab: null },
  { keys: 'Maustaste halten', action: 'zum Mauszeiger fliegen', key: null, tab: null },
  { keys: 'Rechtsklick', action: 'Schub zum Zielpunkt', key: null, tab: null },
  { keys: 'Shift', action: 'Boost (kostet Energie)', key: null, tab: null },
  { keys: 'U', action: 'Upgrades', key: 'u', tab: 'upgrades' },
  { keys: 'B', action: 'Basis-Ausbau', key: 'b', tab: 'base' },
  { keys: 'Q', action: 'Aufträge', key: 'q', tab: 'quests' },
  { keys: 'K', action: 'Karte', key: 'k', tab: 'map' },
  { keys: 'E', action: 'Punkte', key: 'e', tab: 'points' },
  { keys: 'T', action: 'Statistik', key: 't', tab: 'stats' },
  { keys: 'R', action: 'Bestenliste', key: 'r', tab: 'ranking' },
  { keys: 'L', action: 'Missionslog', key: 'l', tab: 'log' },
  { keys: 'C', action: 'Kolonie', key: 'c', tab: 'colony' },
  { keys: 'X', action: 'Signal an Mitspieler', key: null, tab: null },
  { keys: 'F', action: 'Solarwind peilen', key: null, tab: null },
  { keys: 'V', action: 'Lichtflug an/aus', key: null, tab: null },
  { keys: 'P', action: 'Autopilot abbrechen', key: null, tab: null },
  { keys: 'Leertaste', action: 'Zwischensequenz überspringen', key: null, tab: null },
  { keys: 'H', action: 'Hilfe', key: 'h', tab: 'help' },
  { keys: 'M', action: 'Ton an/aus', key: 'm', tab: null },
  { keys: '+ / −', action: 'näher / weiter weg', key: null, tab: null },
  { keys: 'Mausrad', action: 'näher / weiter weg', key: null, tab: null },
  { keys: 'Esc', action: 'Panel schließen', key: null, tab: null },
];

/**
 * Gesten, die keine Taste sind.
 *
 * Sie stehen im README als Fliesstext und werden von FG-39 deshalb **nicht**
 * verglichen: ein Finger hat keinen Zustand vor dem Druecken, und diese Zeilen
 * beschreiben Verhalten, keine Belegung (siehe `.claude/rules/oberflaeche.md`).
 */
export const TOUCH_HINTS: ReadonlyArray<readonly [string, string]> = [
  ['Finger halten', 'zum Finger fliegen'],
  ['Zwei Finger', 'näher / weiter weg'],
  ['Im Baum tippen', 'wählt aus, nochmal tippen kauft'],
];

/** Abgeleitet, nicht zweitgeschrieben — `main.ts` nimmt genau diese Karte. */
export const PANEL_KEYS: Readonly<Record<string, PanelTab>> = Object.fromEntries(
  KEY_BINDINGS.flatMap((binding) => (binding.key && binding.tab ? [[binding.key, binding.tab]] : [])),
);
